import { useState, useContext, MutableRefObject } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { WillEditorContext } from "../will-editor";
import { AnimatePresence } from "framer-motion"
import STKAnimated from "@/components/common/animated";
import STKTextField from "@/components/common/text-field";
import SimpleReactValidator from "simple-react-validator";
import APIService from "@/util/api-service";
import useNotification from "@/lib/useNotification";

type Props = {
    name: string,
    simpleValidator: MutableRefObject<SimpleReactValidator>,
    addNotification: (message: any) => void
}

type Person = {
    key?: number,
    name?: string,
    email?: string,
    phone?: string,
    notifiedByEmail?: boolean,
    notifiedBySMS?: boolean
}

const ContactPeople = ( props: Props ) => {
    
    const { will, setWillField, css } = useContext(WillEditorContext);
    const [people, setPeople] = useState<Person[]>( (will.fields[props.name] ? will.fields[props.name] : [] ));
    const [isSendingEmailNotification, setIsSendingEmailNotification] = useState(-1);
    const [isSendingSMSNotification, setIsSendingSMSNotification] = useState(-1);

    const [, forceRefresh] = useState({});

    const save = ( people: Person[] ) => {
        setWillField( props.name, people );
    }

    const addPerson = () => {
        let peopleState = [...people];
        peopleState.push({
            key: Math.random() * 1000000000000000
        });
        setPeople(peopleState);
        save( peopleState );
        props.simpleValidator.current.purgeFields();
    }


    const removePerson = ( key: number | undefined ) => {
        if ( ! key ) {
            return;
        }
        let peopleState = [...people];
        peopleState = peopleState.filter( ( person ) => {
            return person.key !== key;
        });
        setPeople(peopleState);
        save( peopleState );
    }


    const notifyContactPersonByEmail = ( key: number | undefined, index: number ) => {
        if ( ! key || isSendingEmailNotification != -1 ) {
            return;
        }
        setIsSendingEmailNotification( index );

        if ( ! props.simpleValidator.current.fieldValid(`contact-person-${index}-email`) ) {
            props.simpleValidator.current.showMessageFor(`contact-person-${index}-email`);
            forceRefresh({});
            setIsSendingEmailNotification( -1 );
            return;
        }

        if ( people[index].notifiedByEmail ) {
            setIsSendingEmailNotification( -1 );
            return;
        }
        
        APIService.postWithUidToken('/api/notify/email', {to: people[index].email, willMaker: will.fields.name }).then( (response) => {
            setIsSendingEmailNotification( -1 );
            if ( response.error ) {
                props.addNotification('ERROR');
            } else {
                // Set notified by email to true to make sure only one notification can be sent
                let peopleState = [...people];
                peopleState[index].notifiedByEmail = true;
                setPeople(peopleState);
                save( peopleState )
            }
        });
    }

    const notifyContactPersonBySMS = ( key: number | undefined, index: number ) => {
        if ( ! key || isSendingSMSNotification != -1 ) {
            return;
        }
        setIsSendingSMSNotification( index );

        if ( ! props.simpleValidator.current.fieldValid(`contact-person-${index}-phone`) ) {
            props.simpleValidator.current.showMessageFor(`contact-person-${index}-phone`);
            forceRefresh({});
            setIsSendingSMSNotification( -1 );
            return;
        }

        if ( people[index].notifiedBySMS ) {
            setIsSendingSMSNotification( -1 );
            return;
        }
        
        APIService.postWithUidToken('/api/notify/sms', {to: people[index].phone, willMaker: will.fields.name }).then( (response) => {
            setIsSendingSMSNotification( -1 );
            if ( response.error ) {
                if ( response.error === 'TWILIO_VALIDATION_ERROR' ) {
                    props.addNotification( 'TWILIO_VALIDATION_ERROR' );
                } else {
                    props.addNotification('ERROR');
                }
                
            } else {
                // Set notified by sms to true to make sure only one notification can be sent
                let peopleState = [...people];
                peopleState[index].notifiedBySMS = true;
                setPeople(peopleState);
                save( peopleState )
            }
        });
    }

    const onChange = ( e: any, index:number ) => {
        let peopleState = [...people];
        if ( e.target?.name )
        peopleState[index][e.target?.name as keyof Person] = e.currentTarget.value;

        // if email or phone is changed, you should be able to send new notification
        if ( e.target?.name == 'email' ) {
            peopleState[index].notifiedByEmail = false;
        }
        if ( e.target?.name == 'phone' ) {
            peopleState[index].notifiedBySMS = false;
        }

        save( peopleState );
        props.simpleValidator.current.showMessageFor(`contact-person-${index}-${e.target?.name}`);
    }

    props.simpleValidator.current.purgeFields();

    return(
        
        <div className="mt-6">
            <div>
                <AnimatePresence>
                { people.map( ( person: Person, index: number ) => {

                    const onChangeHelper = ( e:any ) => { onChange(e, index) }
                    const defaultValue = will.fields[props.name][index];

                    return(
                        <STKAnimated key={person.key} exit={{ y:"-20px", opacity: 0 }} type="up-to-down"> 
                            <div className="mb-6">
                                <button className={css.contactPeople__remove} onClick={ () => { removePerson(person.key) } }>
                                    Yhteyshenkilö {index+1}
                                    <span><FontAwesomeIcon icon={["far", "minus"]} /></span>
                                </button>  
                                <STKTextField className="mb-2" 
                                              name="name" 
                                              defaultValue={defaultValue?.name} 
                                              label="Etunimet ja Sukunimi" 
                                              onChange={onChangeHelper}
                                              invalid={props.simpleValidator.current.message( `contact-person-${index}-name`, defaultValue?.name, 'required' )} 
                                />
                                <STKTextField   className="mb-2" 
                                                name="email" 
                                                defaultValue={defaultValue?.email} 
                                                label="Sähköposti" 
                                                onChange={onChangeHelper}
                                                invalid={props.simpleValidator.current.message( `contact-person-${index}-email`, defaultValue?.email, 'email' )} 

                                                button={ ( people[index].email && people[index].email != '' ) ? {
                                                    props: {
                                                        onClick: () => { notifyContactPersonByEmail(person.key, index) },
                                                        disabled: person.notifiedByEmail,
                                                    },
                                                    text: person.notifiedByEmail ? 
                                                            <><FontAwesomeIcon icon={["fas", "check"]} /> Ilmoitettu</> 
                                                            : 
                                                            <>{isSendingEmailNotification === index ? 
                                                                    <FontAwesomeIcon icon={["fas", "spinner"]} spinPulse /> : <FontAwesomeIcon icon={["fal", "paper-plane"]} />} Lähetä ilmoitus</>
                                                }: false}
                                />
                                <STKTextField className="mb-2" 
                                              name="phone" 
                                              defaultValue={defaultValue?.phone} 
                                              label="Puhelinnumero" 
                                              onChange={onChangeHelper} 
                                              invalid={props.simpleValidator.current.message( `contact-person-${index}-phone`, defaultValue?.phone, 'phone2' )}
                                              button={ ( people[index].phone && people[index].phone != '' ) ? {
                                                props: {
                                                    onClick: () => { notifyContactPersonBySMS(person.key, index) },
                                                    disabled: person.notifiedBySMS,
                                                },
                                                text: person.notifiedBySMS ? 
                                                        <><FontAwesomeIcon icon={["fas", "check"]} /> Ilmoitettu</> 
                                                        : 
                                                        <>{isSendingSMSNotification === index ? 
                                                                <FontAwesomeIcon icon={["fas", "spinner"]} spinPulse /> : <FontAwesomeIcon icon={["fal", "paper-plane"]} />} Lähetä ilmoitus</>    
                                            }: false}
                                />
                            </div>
                        </STKAnimated>
                    );
                }) }
                </AnimatePresence>
            </div>
            <button className={css.contactPeople__add} onClick={addPerson}><span><FontAwesomeIcon icon={["far", "plus"]} /></span>Lisää yhteyshenkilö</button>
        </div>
    );

}

export default ContactPeople;